import React, { useCallback, useMemo, useState } from 'react';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useCgTable } from 'modules/Common/hooks/useCgTable';
import CgConfirmDialog from 'modules/Common/ui/CgConfirmDialog';
import AdminPageHeader from '../components/AdminPageHeader';
import { AccessDeniedPanel, ErrorPanel, isForbidden } from '../components/AdminStatePanels';
import OrganizationFilters from '../components/OrganizationFilters';
import OrganizationsTable, { buildHeaders, buildMapRow } from '../components/OrganizationsTable';
import OrganizationFormDialog from '../components/OrganizationFormDialog';
import ManagePlanDialog from '../components/ManagePlanDialog';
import FirmAdminsDialog from '../components/FirmAdminsDialog';
import { useAdminInstances } from '../hooks/useAdminInstances';
import {
  useAdminOrganizations,
  useCreateOrganization,
  useDeleteOrganization,
  useUpdateOrganization,
  useUpdateOrganizationPlan,
} from '../hooks/useAdminOrganizations';

/**
 * The filter shape, at module scope — mirroring SuppliersTable's SUPPLIER_FILTERS.
 * It doubles as useCgTable's URL codec spec and as what "clear filters" resets to,
 * so it must be a stable object identity: the hook captures it once
 * (defaultsRef) and a literal rebuilt each render would reshuffle state.
 *
 * `clerkInstance` is here even on a single-brand deployment, where the control is
 * hidden. It costs nothing — the hook keeps default-valued filters out of both the
 * query and the URL — and it means onboarding a second brand changes only whether
 * the control renders, not the filter shape.
 */
const ORGANIZATION_FILTERS = {
  name: '',
  contactEmail: '',
  EORI: '',
  clerkOrganisationId: '',
  type: '',
  planStatus: '',
  clerkInstance: '',
};

export default function OrganizationsPage() {
  const { instances, isMultiInstance } = useAdminInstances();

  const [formDialog, setFormDialog] = useState(null); // { mode: 'create' | 'edit', org? }
  const [planDialog, setPlanDialog] = useState(null); // org
  const [firmAdminsDialog, setFirmAdminsDialog] = useState(null); // verifier org
  const [deleteTarget, setDeleteTarget] = useState(null); // org

  const createMutation = useCreateOrganization();
  const updateMutation = useUpdateOrganization();
  const deleteMutation = useDeleteOrganization();
  const planMutation = useUpdateOrganizationPlan();

  const isMutating =
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    planMutation.isPending;

  // Memoised because useCgTable's row-mapping memo keys on [safeRows, mapRow]:
  // a fresh identity each render re-maps every row on every render.
  const headers = useMemo(() => buildHeaders(isMultiInstance), [isMultiInstance]);
  // `instances` is memoised inside useAdminInstances, so this identity is stable and the
  // hook's row-mapping memo does not re-map every row on every render.
  const mapRow = useMemo(
    () => buildMapRow(isMultiInstance, instances),
    [isMultiInstance, instances],
  );

  const {
    tableProps,
    filters: form,
    setFilters: handleFormChange,
    resetFilters,
    data,
    loading,
    query,
  } = useCgTable({
    key: 'admin-organizations',
    filters: ORGANIZATION_FILTERS,
    // Straight passthrough — no adapter. The API takes `page` and `limit`
    // (phase 2 §4), which are exactly the names the hook emits.
    useData: ({ filters, pagination }) => useAdminOrganizations({ filters, pagination }),
    select: (d) => ({ rows: d?.organizations ?? [], total: d?.itemCount ?? 0 }),
    headers,
    mapRow,
    defaultLimit: 50, // the hook's own default is 10
    syncUrl: true,
    busy: isMutating,
  });

  const openCreate = useCallback(() => setFormDialog({ mode: 'create' }), []);
  const openEdit = useCallback((org) => setFormDialog({ mode: 'edit', org }), []);

  const handleDelete = async () => {
    await deleteMutation.mutateAsync(deleteTarget._id);
    setDeleteTarget(null);
  };

  // The gate's 403 is a permissions ANSWER, not a failed request: retrying it will
  // never succeed, so it gets its own panel instead of the retry one. The console's
  // own axios instance is what lets this response reach here at all — the customer
  // interceptor would have redirected it to /trial-expired (D9).
  const forbidden = isForbidden(query?.error);

  return (
    <>
      <AdminPageHeader
        eyebrow="Administration"
        title="Organizations"
        meta={
          forbidden || !data
            ? null
            : `${data.itemCount ?? 0} organization${data.itemCount === 1 ? '' : 's'}`
        }
        primaryAction={
          forbidden ? null : { label: 'New organization', icon: faPlus, onClick: openCreate }
        }
      />

      {forbidden ? (
        <AccessDeniedPanel />
      ) : query?.isError ? (
        <ErrorPanel
          title="Could not load organizations"
          onRetry={() => query.refetch()}
        />
      ) : (
        <>
          <OrganizationFilters
            form={form}
            onFormChange={handleFormChange}
            onReset={resetFilters}
            loading={loading}
            onRefresh={() => query?.refetch()}
            instances={instances}
            isMultiInstance={isMultiInstance}
          />

          <OrganizationsTable
            tableProps={tableProps}
            onManagePlan={setPlanDialog}
            onManageFirmAdmins={setFirmAdminsDialog}
            onEdit={openEdit}
            onDelete={setDeleteTarget}
            onCreate={openCreate}
          />
        </>
      )}

      {formDialog ? (
        <OrganizationFormDialog
          mode={formDialog.mode}
          organization={formDialog.org}
          instances={instances}
          isMultiInstance={isMultiInstance}
          createMutation={createMutation}
          updateMutation={updateMutation}
          onClose={() => setFormDialog(null)}
        />
      ) : null}

      {firmAdminsDialog ? (
        <FirmAdminsDialog
          organization={firmAdminsDialog}
          onClose={() => setFirmAdminsDialog(null)}
        />
      ) : null}

      {planDialog ? (
        <ManagePlanDialog
          organization={planDialog}
          mutation={planMutation}
          onClose={() => setPlanDialog(null)}
        />
      ) : null}

      <CgConfirmDialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete organization"
        description={
          deleteTarget
            ? `Delete ${deleteTarget.name}? This soft-deletes the account and its plans, and DELETES the Clerk organization — which cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        loading={deleteMutation.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}
